import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import { BOOKING_CALENDAR_LICENSE_KEY } from "src/app.config";
import { selectAllCalendarEvents } from "src/selectors/bookingCalendar.selectors";

import Loader from "@components/shared/Loader/Loader";
import WarningMessageBox from "@components/Unit/WarningMessageBox/WarningMessageBox";
import { TIME_CONSTANTS } from "@constants/Bookings";
import { DRAWER_HEIGHT } from "@constants/Drawer";
import { DateFormats } from "@enums/shared.enum";
import { EventClickArg } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import enLocale from "@fullcalendar/core/locales/en-gb";
import frLocale from "@fullcalendar/core/locales/fr";
import itLocale from "@fullcalendar/core/locales/it";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import scrollgridPlugin from "@fullcalendar/scrollgrid";
import { useBookingCalendar } from "@hooks/UseBookingCalendar/UseBookingCalendar";
import { useBookingCalendarEvents } from "@hooks/UseBookingCalendar/UseBookingCalendarEvents";
import {
  BookingCalendarProps,
  ParticipantType,
} from "@models/components/booking.model";
import { getLocationType, locationType } from "@models/services/organisation";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";

import BookingParticipantModal from "../BookingParticipantModal/BookingParticipantModal";

const DISABLE_ERROR = true;

const BookingCalendar: FC<BookingCalendarProps> = ({
  bookingLoader,
  slots,
}) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [participant, setParticipant] = useState<ParticipantType | null>(null);

  const [hasError, setHasError] = useState(false);

  const context: [getLocationType, locationType] = useOutletContext();

  const {
    calendarTime,
    fetchCalendarData,
    fetchCalendarParticipant,
    isLoading,
    renderEventContent,
    resources,
    setIsLoading,
  } = useBookingCalendar(slots);

  const {
    getEventClassName,
    handleEventDrop,
    handleEventResize,
    handleTimeSelect,
  } = useBookingCalendarEvents(slots, setHasError, setIsLoading);

  const selectedLanguage = useSelector(
    (state: RootState) => state.user.selectedLanguage
  );

  const selectedDate = useSelector(
    (state: RootState) => state.booking.selectedDate
  );

  const selectedTime = useSelector(
    (state: RootState) => state.booking.selectedTime
  );

  const dispatch = useDispatch();

  const calendarEvents = useSelector(selectAllCalendarEvents);

  const [participantModal, setParticipantModal] = useState(false);

  const toggleParticipantModal = () => setParticipantModal(!participantModal);

  const [stableResources, setStableResources] =
    useState<typeof resources>(resources);
  const [events, setEvents] = useState<typeof calendarEvents>(calendarEvents);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setStableResources(resources);
  }, [isLoading, resources]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setEvents(calendarEvents);
  }, [isLoading, calendarEvents]);

  useEffect(() => {
    setHasError(false);
    fetchCalendarData({ locationId: context[1]?.id });
  }, [selectedLanguage, selectedDate, context[1]?.id, slots?.resources]);

  const handleDatesSet = () => {
    if (
      moment(selectedDate, DateFormats.YMD).isSame(moment(), DateFormats.Day)
    ) {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.scrollToTime(
          moment()
            .subtract(1, DateFormats.Hour)
            .format(DateFormats.TimeWithSeconds)
        );
      }
    }
  };

  const handleEventClick = async (clickInfo: EventClickArg) => {
    const { bookingId, showDrawer, showPopup } = clickInfo.event.extendedProps;

    if (showDrawer) {
      dispatch(
        drawerSlice.actions.setOpenPercent(DRAWER_HEIGHT.DEFAULT_OPEN_PERCENT)
      );
      return;
    }

    if (!showPopup) {
      return;
    }

    const participantObj = await fetchCalendarParticipant(bookingId);

    setParticipant(participantObj);
    toggleParticipantModal();
  };

  return (
    <>
      <div className="fg-dev-booking-calendar mb-3 position-relative">
        <Loader isLoading={isLoading && !bookingLoader} />
        <FullCalendar
          plugins={useMemo(
            () => [resourceTimeGridPlugin, interactionPlugin, scrollgridPlugin],
            []
          )}
          headerToolbar={useMemo(
            () => ({
              center: "",
              left: "",
              right: "",
            }),
            []
          )}
          schedulerLicenseKey={BOOKING_CALENDAR_LICENSE_KEY}
          key={selectedDate}
          initialDate={selectedDate}
          scrollTimeReset={false}
          eventClassNames={getEventClassName}
          ref={calendarRef}
          aspectRatio={1.5}
          locale={selectedLanguage}
          locales={useMemo(() => [enLocale, deLocale, itLocale, frLocale], [])}
          themeSystem="bootstrap5"
          eventOverlap={false}
          selectOverlap={false}
          timeZone="local"
          allDaySlot={false}
          datesSet={handleDatesSet}
          slotDuration={TIME_CONSTANTS.START_OF_DAY_15_MINUTE_MARK}
          snapDuration={TIME_CONSTANTS.START_OF_DAY_15_MINUTE_MARK}
          slotMinTime={calendarTime.start || TIME_CONSTANTS.START_OF_DAY}
          slotMaxTime={calendarTime.end || TIME_CONSTANTS.END_OF_DAY}
          slotLabelFormat={useMemo(
            () => ({
              hour: "2-digit",
              hour12: false,
              minute: "2-digit",
            }),
            []
          )}
          nowIndicator={true}
          initialView="resourceTimeGridDay"
          height={"500px"}
          resources={stableResources}
          editable={false}
          selectable={true}
          selectMirror={true}
          unselectAuto={true}
          longPressDelay={200}
          selectLongPressDelay={200}
          eventResizableFromStart={true}
          dayMaxEvents={true}
          weekends={true}
          events={events}
          select={handleTimeSelect}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          dayMinWidth={200}
          eventResize={handleEventResize}
          eventDrop={handleEventDrop}
        />
      </div>

      <BookingParticipantModal
        participantModal={participantModal}
        toggleParticipantModal={toggleParticipantModal}
        participant={participant}
        setParticipant={participant => setParticipant(participant ?? null)}
        isLoading={isLoading}
      />

      {!DISABLE_ERROR && hasError && !isLoading && !selectedTime && (
        <WarningMessageBox
          message={t("timeSlotDurationUnavailable")}
          className="mt-3"
        />
      )}
    </>
  );
};

export default memo(BookingCalendar);
